import { useContext, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';

import { getBannerThreshold } from '../thresholds.ts';
import { EVENT_SOURCE } from '../../constants.ts';
import { useGetCurrentStorageUsage } from '../../hooks/useGetCurrentStorageUsage.ts';

/**
 * Used for both soft and hard enforcement banners. Should only be initialized once due to mount effect.
 * @returns an object containing analytic events.
 */
export const useBannerAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { currentUsage } = useGetCurrentStorageUsage();
	const { isSiteAdmin } = useContext(SPAViewContext);
	const threshold = getBannerThreshold(currentUsage, ConfluenceEdition.FREE);

	const fireScreenEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'storageEnforcementBanner',
				attributes: {
					isSiteAdmin,
					threshold,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, threshold]);

	const fireLearnMoreEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'storageEnforcementBannerLearnMore',
				source: EVENT_SOURCE,
				attributes: {
					isSiteAdmin,
					threshold,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, threshold]);

	const fireUpgradeEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'storageEnforcementBannerUpgrade',
				source: EVENT_SOURCE,
				attributes: {
					isSiteAdmin,
					threshold,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, threshold]);

	const fireDismissEvent = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'storageEnforcementBannerDismissed',
				source: EVENT_SOURCE,
				attributes: {
					isSiteAdmin,
					threshold,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin, threshold]);

	return {
		fireUpgradeEvent,
		fireLearnMoreEvent,
		fireDismissEvent,
		fireScreenEvent,
	};
};
